import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiStarFill, PiQuotes, PiUserCircle } from "react-icons/pi";
import logger from "@/utils/logger";

interface Testimonial {
  title: string;
  text: string;
  name: string;
  handle: string;
  date: string;
  rating: number;
  avatar: string;
}

export default function HokTestimonials({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const primaryColor = content.colorPalette?.primary || "#684DF4";
  const secondaryColor = content.colorPalette?.secondary || "#8a2be2";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const defaultTestimonials: Testimonial[] = [
    {
      title: "Unique discoveries",
      text: "I ordered a handcrafted chair and it exceeded my expectations! The craftsmanship is top-notch and it looks stunning in my living room. Definitely a place to shop for quality pieces.",
      name: "Emily Johnson",
      handle: "@emily",
      date: "22 March, 2026",
      rating: 5,
      avatar: "/assets/img/testimonial/testi_1_1.png",
    },
    {
      title: "Wonderful Bidding Items",
      text: "I ordered a handcrafted chair and it exceeded my expectations! The craftsmanship is top-notch and it looks stunning in my living room. Definitely a place to shop for quality pieces.",
      name: "John Doe",
      handle: "@john",
      date: "25 March, 2026",
      rating: 5,
      avatar: "/assets/img/testimonial/testi_1_2.png",
    },
    {
      title: "Wonderful Bidding Items",
      text: "I ordered a handcrafted chair and it exceeded my expectations! The craftsmanship is top-notch and it looks stunning in my living room. Definitely a place to shop for quality pieces.",
      name: "John Doe",
      handle: "@john",
      date: "25 March, 2026",
      rating: 5,
      avatar: "/assets/img/testimonial/testi_1_3.png",
    },
    {
      title: "Exceptional Theme Finds",
      text: "I ordered a handcrafted chair and it exceeded my expectations! The craftsmanship is top-notch and it looks stunning in my living room. Definitely a place to shop for quality pieces.",
      name: "Jarin Smith",
      handle: "@Jarin",
      date: "19 March, 2026",
      rating: 5,
      avatar: "/assets/img/testimonial/testi_1_4.png",
    },
    {
      title: "Outstanding course layouts",
      text: "The template designs are very clean and optimized for conversion. My sales increased by 30% after switching to Instacks marketplace themes. Very satisfied!",
      name: "David Miller",
      handle: "@david",
      date: "12 April, 2026",
      rating: 5,
      avatar: "/assets/img/testimonial/testi_1_1.png",
    },
    {
      title: "Great customer support",
      text: "Whenever I faced an issue with integration, the support team responded within minutes. They helped me customize the checkout flow quickly. Highly recommended!",
      name: "Sarah Connor",
      handle: "@sarah",
      date: "05 April, 2026",
      rating: 5,
      avatar: "/assets/img/testimonial/testi_1_2.png",
    },
    {
      title: "Best coding standards",
      text: "As a developer, I really appreciate the codebase quality. It's clean, well-commented, and very easy to extend. The page loads incredibly fast too.",
      name: "Michael Scott",
      handle: "@michael",
      date: "28 March, 2026",
      rating: 5,
      avatar: "/assets/img/testimonial/testi_1_3.png",
    },
    {
      title: "Highly recommended platform",
      text: "Instacks provides the best marketplace infrastructure I've ever used. The payout process is smooth and the vendor dashboard is extremely detailed.",
      name: "Dwight Schrute",
      handle: "@dwight",
      date: "15 March, 2026",
      rating: 5,
      avatar: "/assets/img/testimonial/testi_1_4.png",
    },
  ];

  const testimonials: Testimonial[] = content.testimonials && content.testimonials.length >= 4 ? content.testimonials : defaultTestimonials;

  const [startIndex, setStartIndex] = React.useState(0);

  React.useEffect(() => {
    if (startIndex > testimonials.length - 4) {
      setStartIndex(Math.max(0, testimonials.length - 4));
    }
  }, [testimonials.length, startIndex]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setStartIndex((prev) => {
        const next = prev + 1;
        return next > testimonials.length - 4 ? 0 : next;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, [testimonials.length, startIndex]);

  const updateTestimonial = (idx: number, field: keyof Testimonial, value: string) => {
    if (!onContentChange) return;
    const newTestimonials = [...testimonials];
    newTestimonials[idx] = { ...newTestimonials[idx], [field]: value };
    onContentChange({ ...content, testimonials: newTestimonials });
  };

  const addTestimonial = () => {
    if (!onContentChange) return;
    const newTestimonials = [...testimonials, {
      title: "New Review",
      text: "Add your review text here.",
      name: "Customer Name",
      handle: "@handle",
      date: "01 January, 2026",
      rating: 5,
      avatar: "/assets/img/testimonial/testi_1_1.png",
    }];
    onContentChange({ ...content, testimonials: newTestimonials });
  };

  const removeTestimonial = (idx: number) => {
    if (!onContentChange) return;
    const newTestimonials = testimonials.filter((_, i) => i !== idx);
    onContentChange({ ...content, testimonials: newTestimonials });
  };

  const renderHighlightedTitle = (text: string) => {
    if (!text) return null;
    const words = text.trim().split(/\s+/);
    if (words.length <= 1) {
      return <span className="text-[#684DF4]" style={{ color: primaryColor }}>{text}</span>;
    }
    const lastWord = words.pop();
    const remainingText = words.join(" ");
    return (
      <>
        {remainingText}{" "}
        <span className="text-[#684DF4]" style={{ color: primaryColor }}>
          {lastWord}
        </span>
      </>
    );
  };
  const visibleTestimonials = testimonials;

  return (
    <section
      className={`relative overflow-hidden w-full py-10 sm:py-14 ${isSelected ? "ring-2 ring-blue-500 relative z-20" : ""
        }`}
      style={{
        fontFamily,
        background: "url('/assets/img/bg/testi_bg_1.png') no-repeat center / cover, #F9FAFB",
      }}
    >
      <style dangerouslySetInnerHTML={{
        __html: `
        .testimonial-slider {
          --slide-width: calc(100% - 0px);
        }
        @media (min-width: 640px) {
          .testimonial-slider {
            --slide-width: calc(50% - 12px);
          }
        }
        @media (min-width: 1024px) {
          .testimonial-slider {
            --slide-width: calc(25% - 18px);
          }
        }
      `}} />

      {/* Decorative Blur Shapes */}
      {/* Blue Capsule Shape (Top Left) */}
      <div className="absolute top-6 left-6 w-8 h-16 bg-[#3B82F6]/20 rounded-full rotate-45 pointer-events-none z-0" />
      {/* Left-Center Purple Organic Shape */}
      <div className="absolute top-[20%] -left-16 w-52 h-44 bg-gradient-to-br from-[#EEF2FF] to-[#E3DCFF] rounded-r-[150px] rounded-l-[50px] rotate-[12deg] pointer-events-none z-0" />
      {/* Right Green Shape */}
      <div className="absolute top-36 -right-12 w-48 h-36 bg-[#E8FFF0] rounded-l-[140px] rounded-r-[80px] rotate-[-12deg] pointer-events-none z-0" />
      {/* Bottom Pink Shape */}
      <div className="absolute -bottom-12 left-[20%] w-60 h-40 bg-gradient-to-tr from-[#FFF0F5] to-[#FFE4E1] rounded-full blur-3xl opacity-80 pointer-events-none z-0" />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
        {/* Section header */}
        <div className="text-center mb-8">
          <div className="inline-block border border-[#684DF4]/20 text-[#684DF4] bg-[#684DF4]/5 px-5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
            <EditableText
              value={content.subtitle || "Testimonials"}
              onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
              as="span"
              placeholder="Subtitle"
            />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-gray-900 leading-tight mb-4 tracking-tight">
            {renderHighlightedTitle(content.title || "Client Acknowledgement Reviews")}
            <div className="hidden">
              <EditableText
                value={content.title || "Client Acknowledgement Reviews"}
                onChange={(val) => onContentChange?.({ ...content, title: val })}
                as="span"
                placeholder="Title"
              />
            </div>
          </h2>
          <p className="text-gray-500 text-sm sm:text-base max-w-xl mx-auto font-normal">
            <EditableText
              value={content.description || "Join us for an exhilarating live auction experience where theme meets excitement."}
              onChange={(val) => onContentChange?.({ ...content, description: val })}
              as="span"
              placeholder="Description"
            />
          </p>
        </div>

        {/* Testimonials Slider Wrapper */}
        <div className="relative w-full overflow-hidden pt-4 pb-10">
          <div
            className="flex gap-6 transition-transform duration-500 ease-in-out testimonial-slider"
            style={{
              transform: `translateX(calc(-${startIndex} * (var(--slide-width) + 1.5rem)))`,
            }}
          >
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className={`w-[var(--slide-width)] flex-shrink-0 group relative bg-white rounded-[24px] p-6 sm:p-7 shadow-[0_8px_30px_rgba(0,0,0,0.025)] hover:shadow-xl transition-all duration-300 flex flex-col justify-between ${Math.abs(idx - startIndex) % 2 === 1 ? "lg:translate-y-8" : "lg:translate-y-0"
                  }`}
              >
                <div>
                  {isSelected && (
                    <button
                      onClick={() => removeTestimonial(idx)}
                      className="absolute top-3 right-3 z-10 h-7 w-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs font-bold"
                      title="Remove"
                    >
                      ×
                    </button>
                  )}

                  {/* Quote and Title */}
                  <div className="flex items-start gap-2.5 mb-4">
                    <PiQuotes className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#684DF4]" style={{ color: primaryColor }} />
                    <h4 className="text-base font-bold text-gray-900 tracking-tight leading-snug">
                      <EditableText
                        value={testimonial.title}
                        onChange={(val) => updateTestimonial(idx, "title", val)}
                        as="span"
                        placeholder="Review Title"
                      />
                    </h4>
                  </div>

                  {/* Review Text */}
                  <p className="text-slate-500 text-sm leading-relaxed mb-6 font-normal">
                    <EditableText
                      value={testimonial.text}
                      onChange={(val) => updateTestimonial(idx, "text", val)}
                      as="span"
                      placeholder="Review text..."
                    />
                  </p>
                </div>

                {/* Footer: profile and meta */}
                <div>
                  <div className="w-full h-px bg-gray-100 mb-5" />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex-shrink-0 group/avatar">
                        {testimonial.avatar ? (
                          <img
                            src={testimonial.avatar}
                            alt={testimonial.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <PiUserCircle className="w-full h-full text-gray-400" />
                        )}
                        {isSelected && (
                          <label className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 rounded-full hover:bg-black/80 cursor-pointer transition-all opacity-0 group-hover/avatar:opacity-100">
                            <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  const toast = (await import("react-hot-toast")).default;
                                  const homepageApi = (await import("@/services/homepageApi")).default;
                                  const toastId = toast.loading("Uploading avatar...");
                                  try {
                                    const response = await homepageApi.uploadImage(file);
                                    const url = response.data?.url || response.url;
                                    if (url) {
                                      updateTestimonial(idx, "avatar", url);
                                      toast.success("Avatar updated!", { id: toastId });
                                    } else {
                                      toast.error("Failed to upload.", { id: toastId });
                                    }
                                  } catch (err) {
                                    toast.error("Upload failed", { id: toastId });
                                    logger.error(err);
                                  }
                                }
                              }}
                            />
                          </label>
                        )}
                      </div>
                      <div className="flex flex-col text-left">
                        <h5 className="text-xs font-bold text-gray-900 leading-tight">
                          <EditableText
                            value={testimonial.name}
                            onChange={(val) => updateTestimonial(idx, "name", val)}
                            as="span"
                            placeholder="Name"
                          />
                        </h5>
                        <span className="text-[10px] font-semibold text-[#684DF4]" style={{ color: primaryColor }}>
                          <EditableText
                            value={testimonial.handle}
                            onChange={(val) => updateTestimonial(idx, "handle", val)}
                            as="span"
                            placeholder="@handle"
                          />
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1">
                      <span className="text-[10px] text-gray-400 font-medium">
                        <EditableText
                          value={testimonial.date}
                          onChange={(val) => updateTestimonial(idx, "date", val)}
                          as="span"
                          placeholder="Date"
                        />
                      </span>
                      <div className="flex items-center gap-0.5 text-amber-400">
                        {Array.from({ length: testimonial.rating || 5 }).map((_, s) => (
                          <PiStarFill key={s} className="w-3 h-3" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Add card for admin if in edit mode */}
            {isSelected && (
              <button
                onClick={addTestimonial}
                className="w-[var(--slide-width)] flex-shrink-0 flex flex-col items-center justify-center min-h-[220px] rounded-[24px] border-2 border-dashed border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100 hover:border-gray-300 hover:text-gray-600 transition-all self-stretch"
              >
                <span className="text-3xl mb-1 opacity-50">+</span>
                <span className="text-xs font-semibold opacity-70">Add Testimonial</span>
              </button>
            )}
          </div>
        </div>

        {/* Slider Controls */}
        {testimonials.length > 4 && (
          <div className="flex items-center justify-center gap-4 mt-4 relative z-10">
            <button
              onClick={() => setStartIndex(prev => Math.max(prev - 1, 0))}
              disabled={startIndex === 0}
              className="w-10 h-10 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-gray-700 select-none">
              {startIndex + 4} / {testimonials.length}
            </span>
            <button
              onClick={() => setStartIndex(prev => Math.min(prev + 1, testimonials.length - 4))}
              disabled={startIndex >= testimonials.length - 4}
              className="w-10 h-10 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
